import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Globe2, Search, Download, FileSpreadsheet, ClipboardCopy, Save, Trash2, RefreshCw, FileText } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

import {
  fetchCountries,
  fetchCountryData,
  saveDataset,
  listSavedDatasets,
  deleteSavedDataset,
  SECTION_A_LABELS,
  SECTION_B_LABELS,
  fmtNumber,
  buildJsonExport,
  buildCsv,
} from "@/lib/data-collector";

const NA_LABEL = "Donnée non disponible — saisie manuelle requise";

/* ---------------------------------------------------------------- */
/* Form helpers                                                      */
/* ---------------------------------------------------------------- */
function emptyDataset(labelsA, labelsB) {
  const sectionA = Object.fromEntries(
    Object.keys(labelsA).map((k) => [k, { value: null, year: null, source: NA_LABEL, available: false }])
  );
  const sectionB = Object.fromEntries(
    Object.keys(labelsB).map((k) => [k, { value: null, year: null, source: NA_LABEL, available: false }])
  );
  return { section_a_macro: sectionA, section_b_demo: sectionB, sources: [], reference_year: null };
}

/* ---------------------------------------------------------------- */
/* Field row                                                         */
/* ---------------------------------------------------------------- */
function FieldRow({ k, label, val, onChange, testIdPrefix }) {
  const isAvailable = val?.available;
  return (
    <div className="grid grid-cols-12 items-center gap-2 py-2">
      <div className="col-span-5 text-sm font-medium" data-testid={`${testIdPrefix}-${k}-label`}>
        {label}
      </div>
      <div className="col-span-4">
        <Input
          type="number"
          step="any"
          value={val?.value ?? ""}
          onChange={(e) =>
            onChange(k, {
              ...val,
              value: e.target.value === "" ? null : Number(e.target.value),
              available: e.target.value !== "",
            })
          }
          placeholder="—"
          data-testid={`${testIdPrefix}-${k}-input`}
          className="h-9"
        />
      </div>
      <div className="col-span-1">
        <Input
          type="number"
          value={val?.year ?? ""}
          onChange={(e) =>
            onChange(k, { ...val, year: e.target.value === "" ? null : Number(e.target.value) })
          }
          placeholder="Année"
          data-testid={`${testIdPrefix}-${k}-year`}
          className="h-9 text-xs"
        />
      </div>
      <div className="col-span-2 truncate text-[11px] text-muted-foreground" title={val?.source || ""}>
        {isAvailable ? (
          <Badge variant="secondary" className="text-[10px]">
            {val?.source ? `${val.source}` : "—"}
          </Badge>
        ) : (
          <span className="italic text-amber-600 dark:text-amber-400">Saisie manuelle</span>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Main page                                                         */
/* ---------------------------------------------------------------- */
export default function DataCollector() {
  const [countries, setCountries] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [dataset, setDataset] = useState(emptyDataset(SECTION_A_LABELS, SECTION_B_LABELS));
  const [loading, setLoading] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const reportRef = useRef(null);

  /* Initial load: countries + saved datasets */
  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([fetchCountries(), listSavedDatasets().catch(() => [])]);
        setCountries(c);
        setSavedList(s);
      } catch (e) {
        toast.error("Impossible de charger la liste des pays.");
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!search) return countries.slice(0, 50);
    const s = search.toLowerCase();
    return countries.filter((c) => c.name.toLowerCase().includes(s) || c.iso3?.toLowerCase().includes(s)).slice(0, 50);
  }, [countries, search]);

  async function selectCountry(c) {
    setSelectedCountry(c);
    setShowReport(false);
    setLoading(true);
    try {
      const d = await fetchCountryData(c.iso3);
      setDataset({
        section_a_macro: d.section_a_macro || {},
        section_b_demo: d.section_b_demo || {},
        sources: d.sources || [],
        reference_year: extractYear(d) || new Date().getFullYear() - 1,
      });
      toast.success(`${c.name} — données chargées.`);
    } catch (e) {
      toast.error("Échec du fetch automatique. Tous les champs restent éditables.");
      setDataset(emptyDataset(SECTION_A_LABELS, SECTION_B_LABELS));
    } finally {
      setLoading(false);
    }
  }

  function extractYear(d) {
    const all = Object.values({ ...(d.section_a_macro || {}), ...(d.section_b_demo || {}) });
    const years = all.map((v) => v?.year).filter(Boolean);
    return years.length ? Math.max(...years) : null;
  }

  function updateField(section, key, val) {
    setDataset((prev) => ({ ...prev, [section]: { ...prev[section], [key]: val } }));
  }

  /* Auto-calculs ratios */
  const ratios = useMemo(() => {
    const debt = Number(dataset.section_a_macro?.total_public_debt?.value || 0);
    const gdp = Number(dataset.section_a_macro?.total_gdp?.value || 0);
    const pop = Number(dataset.section_b_demo?.total_population?.value || 0);
    const act = Number(dataset.section_b_demo?.active_population?.value || 0);
    return {
      debt_to_gdp: gdp > 0 ? (debt / gdp) * 100 : null,
      debt_per_capita: pop > 0 ? debt / pop : null,
      debt_per_active: act > 0 ? debt / act : null,
    };
  }, [dataset]);

  /* ---- Generate report ---- */
  function generateReport() {
    if (!selectedCountry) {
      toast.warning("Sélectionnez d'abord un pays.");
      return;
    }
    setShowReport(true);
    setTimeout(() => reportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }

  /* ---- Downloads ---- */
  async function downloadPdf() {
    if (!reportRef.current) return;
    const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: "#ffffff" });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const w = 210;
    const h = (canvas.height * w) / canvas.width;
    let pos = 0;
    const pageH = 297;
    if (h <= pageH) {
      pdf.addImage(img, "PNG", 0, 0, w, h);
    } else {
      while (pos < h) {
        pdf.addImage(img, "PNG", 0, -pos, w, h);
        pos += pageH;
        if (pos < h) pdf.addPage();
      }
    }
    pdf.save(`vitae-data-${selectedCountry.iso3}.pdf`);
    toast.success("PDF généré.");
  }

  function downloadCsv() {
    const csv = buildCsv(selectedCountry, dataset);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vitae-data-${selectedCountry.iso3}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV téléchargé (compatible import Vitae-publica).");
  }

  function copyJson() {
    const json = buildJsonExport(selectedCountry, dataset);
    navigator.clipboard.writeText(JSON.stringify(json, null, 2));
    toast.success("JSON copié dans le presse-papiers.");
  }

  async function saveProfile() {
    if (!selectedCountry) return;
    try {
      const saved = await saveDataset({
        country_iso3: selectedCountry.iso3,
        country_name: selectedCountry.name,
        flag_url: selectedCountry.flag_url,
        reference_year: dataset.reference_year,
        section_a_macro: dataset.section_a_macro,
        section_b_demo: dataset.section_b_demo,
      });
      setSavedList((x) => [saved, ...x]);
      toast.success(`Sauvegardé pour ${selectedCountry.name}.`);
    } catch (e) {
      toast.error("Échec de la sauvegarde.");
    }
  }

  async function loadSaved(s) {
    setSelectedCountry({ name: s.country_name, iso3: s.country_iso3, flag_url: s.flag_url });
    setDataset({
      section_a_macro: s.section_a_macro || {},
      section_b_demo: s.section_b_demo || {},
      sources: ["Source : sauvegarde utilisateur"],
      reference_year: s.reference_year,
    });
    setShowReport(false);
    toast.success(`Chargé : ${s.country_name}`);
  }

  async function deleteSaved(id) {
    try {
      await deleteSavedDataset(id);
      setSavedList((x) => x.filter((s) => s.id !== id));
      toast.success("Supprimé.");
    } catch (e) {
      toast.error("Suppression impossible.");
    }
  }

  /* ----------------------------------------------------------------- */
  return (
    <div className="space-y-6 px-4 py-6 lg:px-8" data-testid="data-collector-page">
      <div className="flex items-center gap-3">
        <Globe2 className="h-7 w-7 text-brand-blue dark:text-brand-gold" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Collecteur de Données Pays</h1>
          <p className="text-sm text-muted-foreground">
            Sélectionnez un pays — les indicateurs macro-économiques et démographiques seront récupérés
            automatiquement (Banque mondiale, REST Countries) puis librement éditables.
          </p>
        </div>
      </div>

      {/* Country selector */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Search className="h-4 w-4" /> Étape 1 — Sélectionnez un pays
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input
            placeholder="Rechercher un pays (nom ou code ISO3)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="dc-country-search"
            className="max-w-md"
          />
          <div className="grid max-h-64 grid-cols-2 gap-1 overflow-y-auto rounded border p-2 sm:grid-cols-3 md:grid-cols-4">
            {filtered.map((c) => (
              <button
                key={c.iso3}
                onClick={() => selectCountry(c)}
                data-testid={`dc-country-${c.iso3}`}
                className={`flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition hover:bg-accent ${
                  selectedCountry?.iso3 === c.iso3 ? "bg-accent ring-1 ring-brand-blue" : ""
                }`}
              >
                {c.flag_url && <img src={c.flag_url} alt="" className="h-3 w-5 rounded-sm border" />}
                <span className="truncate">{c.name}</span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Saved list */}
      {savedList.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Mes Pays Sauvegardés ({savedList.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {savedList.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-2 rounded-full border bg-card px-3 py-1.5 text-sm"
                  data-testid={`dc-saved-${s.id}`}
                >
                  {s.flag_url && <img src={s.flag_url} alt="" className="h-3 w-5 rounded-sm border" />}
                  <button onClick={() => loadSaved(s)} className="hover:underline">
                    {s.country_name}
                  </button>
                  <button onClick={() => deleteSaved(s.id)} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected — fields */}
      {selectedCountry && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {selectedCountry.flag_url && (
                <img src={selectedCountry.flag_url} alt="" className="h-5 w-7 rounded-sm border" />
              )}
              {selectedCountry.name}
              {selectedCountry.currency_code && (
                <Badge variant="outline" className="ml-2">{selectedCountry.currency_code}</Badge>
              )}
              {loading && <RefreshCw className="ml-auto h-4 w-4 animate-spin text-muted-foreground" />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Section A */}
            <div>
              <h3 className="mb-1 text-sm font-bold uppercase tracking-wider text-brand-blue dark:text-brand-gold">
                A. Données macro-économiques (dette)
              </h3>
              <div className="grid grid-cols-12 gap-2 border-b pb-1 text-[11px] font-semibold uppercase text-muted-foreground">
                <div className="col-span-5">Indicateur</div>
                <div className="col-span-4">Valeur</div>
                <div className="col-span-1">Année</div>
                <div className="col-span-2">Source</div>
              </div>
              {Object.entries(SECTION_A_LABELS).map(([k, label]) => (
                <FieldRow
                  key={k}
                  k={k}
                  label={label}
                  val={dataset.section_a_macro?.[k]}
                  onChange={(kk, v) => updateField("section_a_macro", kk, v)}
                  testIdPrefix="dc-a"
                />
              ))}
            </div>

            <Separator />

            {/* Section B */}
            <div>
              <h3 className="mb-1 text-sm font-bold uppercase tracking-wider text-brand-blue dark:text-brand-gold">
                B. Données démographiques
              </h3>
              <div className="grid grid-cols-12 gap-2 border-b pb-1 text-[11px] font-semibold uppercase text-muted-foreground">
                <div className="col-span-5">Indicateur</div>
                <div className="col-span-4">Valeur</div>
                <div className="col-span-1">Année</div>
                <div className="col-span-2">Source</div>
              </div>
              {Object.entries(SECTION_B_LABELS).map(([k, label]) => (
                <FieldRow
                  key={k}
                  k={k}
                  label={label}
                  val={dataset.section_b_demo?.[k]}
                  onChange={(kk, v) => updateField("section_b_demo", kk, v)}
                  testIdPrefix="dc-b"
                />
              ))}
            </div>

            <Separator />

            {/* Auto-ratios */}
            <div>
              <h3 className="mb-2 text-sm font-bold uppercase tracking-wider text-brand-blue dark:text-brand-gold">
                Ratios calculés automatiquement
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <RatioCard label="Dette / PIB" value={ratios.debt_to_gdp} unit="%" />
                <RatioCard label="Dette par habitant" value={ratios.debt_per_capita} unit={selectedCountry.currency_code} />
                <RatioCard label="Dette par contribuable actif" value={ratios.debt_per_active} unit={selectedCountry.currency_code} />
              </div>
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={generateReport} data-testid="dc-generate-report">
                <FileText className="mr-2 h-4 w-4" /> Générer le Rapport
              </Button>
              <Button variant="outline" onClick={saveProfile} data-testid="dc-save-profile">
                <Save className="mr-2 h-4 w-4" /> Sauvegarder pour ce pays
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report preview */}
      {showReport && selectedCountry && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="dc-download-bar">
            <Button onClick={downloadPdf} data-testid="dc-download-pdf">
              <Download className="mr-2 h-4 w-4" /> Télécharger en PDF
            </Button>
            <Button variant="outline" onClick={downloadCsv} data-testid="dc-download-csv">
              <FileSpreadsheet className="mr-2 h-4 w-4" /> Télécharger en CSV (Vitae-publica)
            </Button>
            <Button variant="outline" onClick={copyJson} data-testid="dc-copy-json">
              <ClipboardCopy className="mr-2 h-4 w-4" /> Copier les données JSON
            </Button>
          </div>

          <div
            ref={reportRef}
            className="rounded-lg border bg-white p-6 text-foreground shadow-sm dark:bg-card"
            data-testid="dc-report"
          >
            <div className="mb-4 flex items-center gap-3 border-b pb-3">
              {selectedCountry.flag_url && (
                <img src={selectedCountry.flag_url} alt="" className="h-10 w-14 rounded-sm border" />
              )}
              <div>
                <h2 className="text-xl font-bold">VITAE-PUBLICA™ — Fiche Pays</h2>
                <div className="text-sm text-muted-foreground">
                  {selectedCountry.name} ({selectedCountry.iso3})
                  {dataset.reference_year ? ` · Année de référence : ${dataset.reference_year}` : ""}
                </div>
              </div>
            </div>

            <h3 className="mt-2 text-sm font-bold uppercase">A — Macro-économique</h3>
            <table className="mt-2 w-full text-sm">
              <tbody>
                {Object.entries(SECTION_A_LABELS).map(([k, label]) => (
                  <tr key={k} className="border-b last:border-b-0">
                    <td className="py-1.5 pr-2">{label}</td>
                    <td className="py-1.5 text-right font-mono">
                      {fmtNumber(dataset.section_a_macro?.[k]?.value)}
                    </td>
                    <td className="w-16 py-1.5 pl-2 text-right text-xs text-muted-foreground">
                      {dataset.section_a_macro?.[k]?.year || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3 className="mt-4 text-sm font-bold uppercase">B — Démographique</h3>
            <table className="mt-2 w-full text-sm">
              <tbody>
                {Object.entries(SECTION_B_LABELS).map(([k, label]) => (
                  <tr key={k} className="border-b last:border-b-0">
                    <td className="py-1.5 pr-2">{label}</td>
                    <td className="py-1.5 text-right font-mono">
                      {fmtNumber(dataset.section_b_demo?.[k]?.value)}
                    </td>
                    <td className="w-16 py-1.5 pl-2 text-right text-xs text-muted-foreground">
                      {dataset.section_b_demo?.[k]?.year || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3 className="mt-4 text-sm font-bold uppercase">Ratios calculés</h3>
            <table className="mt-2 w-full text-sm">
              <tbody>
                <tr className="border-b">
                  <td className="py-1.5">Dette / PIB</td>
                  <td className="py-1.5 text-right font-mono">{fmtNumber(ratios.debt_to_gdp)} %</td>
                </tr>
                <tr className="border-b">
                  <td className="py-1.5">Dette par habitant</td>
                  <td className="py-1.5 text-right font-mono">
                    {fmtNumber(ratios.debt_per_capita)} {selectedCountry.currency_code}
                  </td>
                </tr>
                <tr>
                  <td className="py-1.5">Dette par contribuable actif</td>
                  <td className="py-1.5 text-right font-mono">
                    {fmtNumber(ratios.debt_per_active)} {selectedCountry.currency_code}
                  </td>
                </tr>
              </tbody>
            </table>

            {dataset.sources?.length > 0 && (
              <div className="mt-4 text-xs text-muted-foreground">
                <strong>Sources :</strong> {dataset.sources.join(" · ")}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function RatioCard({ label, value, unit }) {
  return (
    <div className="rounded-lg border bg-card/50 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-lg font-semibold">
        {fmtNumber(value)} <span className="text-xs text-muted-foreground">{unit}</span>
      </div>
    </div>
  );
}
